import { useState } from 'react';
import useReveal from '../hooks/useReveal';

/** Bundled hero image first (reliable); remote fallbacks if file missing. */
const STORY_CHEF_IMAGES = [
  '/images/story-chef.jpg',
  'https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1400&q=85',
];

export default function StorySection() {
  const revealRef = useReveal();
  const [storyImageIdx, setStoryImageIdx] = useState(0);
  const [imageUnavailable, setImageUnavailable] = useState(false);
  const stats = [
    { num: '100+', label: 'Dishes' },
    { num: '4.9★', label: 'Rating' },
    { num: '15 min', label: 'Ready Time' },
    { num: '5 yrs', label: 'In Seychelles' },
  ];

  return (
    <section id="story" className="section" style={{ background: 'var(--cream)' }}>
      <div className="section-inner">
        <div
          ref={revealRef}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '4rem',
            alignItems: 'center',
          }}
          className="story-grid reveal"
        >
          {/* Visual side */}
          <div style={{ position: 'relative' }}>
            <div
              style={{
                width: '100%',
                maxWidth: 480,
                aspectRatio: '4 / 3',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-hover)',
                position: 'relative',
                background: 'var(--teal-pale)',
              }}
            >
              {!imageUnavailable ? (
                <img
                  src={STORY_CHEF_IMAGES[storyImageIdx]}
                  alt="Thai chef preparing fresh food"
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  onError={() => {
                    if (storyImageIdx < STORY_CHEF_IMAGES.length - 1) {
                      setStoryImageIdx((idx) => idx + 1);
                    } else {
                      setImageUnavailable(true);
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center 32%',
                    display: 'block',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #0f5a68 0%, #1a7a8a 55%, #2d99ab 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(255,255,255,0.92)',
                    fontSize: '4.5rem',
                  }}
                  aria-hidden="true"
                >
                  🍳
                </div>
              )}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(9,64,77,0.55) 0%, transparent 60%)',
              }} />
            </div>

            {/* Accent block */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                width: 140, height: 140,
                background: 'var(--red)',
                borderRadius: 'var(--radius)',
                bottom: -16, right: -16,
                zIndex: -1,
                opacity: .55,
              }}
            />

            {/* Stats grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginTop: '1.25rem',
            }}>
              {stats.map((s) => (
                <div key={s.label} style={{
                  background: 'white',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1.1rem',
                  textAlign: 'center',
                  boxShadow: 'var(--shadow-card)',
                }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.7rem', fontWeight: 900, color: 'var(--teal)', display: 'block' }}>
                    {s.num}
                  </span>
                  <span style={{ fontSize: 'var(--text-label-medium-size)', lineHeight: 'var(--text-label-medium-line)', color: 'var(--gray)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Text side */}
          <div style={{ animationDelay: '.15s' }}>
            <span className="section-label">Our Story</span>
            <h2 className="section-title">
              Paradise Plates<br />on Beau Vallon Beach
            </h2>
            <p style={{ color: 'var(--gray)', lineHeight: 'var(--text-body-large-line)', marginBottom: '1.25rem', fontSize: 'var(--text-body-large-size)' }}>
              Thai Express began with a simple founding spirit: to share the soul of Thai cuisine in one of the most beautiful places on earth. Our small team fell in love with Mahé’s turquoise water, soft sand, and the easy rhythm of life along Beau Vallon — and imagined a kiosk where bold Bangkok flavours could meet island sunshine.
            </p>
            <p style={{ color: 'var(--gray)', lineHeight: 'var(--text-body-large-line)', marginBottom: '1.25rem', fontSize: 'var(--text-body-large-size)' }}>
              We believe great food should feel like a little escape. Every pad Thai, every simmering curry, is cooked with the same care you would find in a family kitchen: fragrant herbs, coconut milk, chilli where it belongs, and produce from Seychellois growers whenever the seasons allow.
            </p>
            <p style={{ color: 'var(--gray)', lineHeight: 'var(--text-body-large-line)', marginBottom: '1.75rem', fontSize: 'var(--text-body-large-size)' }}>
              Whether you are strolling in from the beach or heading home after a day on the water, order from our kiosk and pick up food that is hot, fresh, and ready when you are — no queue, just the taste of Thailand with the Indian Ocean at your back.
            </p>
            <a
              href="#menu"
              className="btn btn-teal"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Explore the Menu
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .story-grid { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
        }
      `}</style>
    </section>
  );
}
